import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { queryClient } from '@/App';
import { useGetUser } from '@/api/user/user.hooks';
import { useTracking } from '@/hooks/useTracking';
import { NewAuthContext } from '@/providers/AuthProvider/NewAuthProvider';
import {
  createAuthApiClient,
  createAuthUserApiClient,
} from '@/providers/AuthProvider/createAuthApiClient';
import { assertIsDefined } from '@/helpers/assertions';
import { PendingRedirect, ReturnTo } from '@/helpers/redirectTo';
import { isKnownRoute, isRouteOrDescendant } from '@/routes/routePatterns';
import { useLogInUser, useRefreshToken } from '@/api/auth/auth.hooks';
import { ELocalStorage, ESessionStorage } from '@/types/storage';
import { ILogInRequest } from '@/api/auth/auth.types';
import { INewAuthContext, TLogoutReason } from '@/providers/AuthProvider/NewAuthProvider.types';
import { IUser } from '@/types/user';
import { ERoutes } from '@/types/routes';

// A logout already ran in this document and no login has succeeded since. Every request
// in flight when a session dies comes back 401 and calls logOut again, and those later
// calls would capture an auth_logout with the identity and session start the first one
// has already cleared.
let hasLoggedOut = false;

// A full document load, not a router navigation: it refetches index.html so a tab that
// outlived a deployment picks up the current bundle instead of requesting chunk hashes
// the host has already deleted.
const redirectToLoginWithFullLoad = () => {
  const currentRoute = window.location.pathname + window.location.search + window.location.hash;

  if (currentRoute === ERoutes.LOGIN) return;

  ReturnTo.set(currentRoute);

  window.location.replace(ERoutes.LOGIN);
};

const getStoredToken = () =>
  localStorage.getItem(ELocalStorage.ACCESS_TOKEN) ||
  sessionStorage.getItem(ELocalStorage.ACCESS_TOKEN) ||
  null;

const getStoredUser = () => {
  const currentUserFromStorage =
    localStorage.getItem(ELocalStorage.USER) || sessionStorage.getItem(ELocalStorage.USER);

  return currentUserFromStorage ? (JSON.parse(currentUserFromStorage) as IUser) : null;
};

const clearStorage = (storage: Storage) => {
  storage.removeItem(ELocalStorage.USER);
  storage.removeItem(ELocalStorage.ACCESS_TOKEN);
};

const clearSessionStartTime = () => {
  localStorage.removeItem(ELocalStorage.AUTH_SESSION_STARTED_AT);
  sessionStorage.removeItem(ESessionStorage.AUTH_SESSION_STARTED_AT);
};

// A stored user predating the null-means-unlimited trackedCounterparties contract cannot be
// upgraded in place: its allowlist is either missing or still carries the legacy
// maxTrackedCounterparties pairing, where an empty list meant unlimited and would now lock
// every entity. Drop the session at import time, before any state initializer reads it.
const discardOutdatedStoredSession = () => {
  const storedUser =
    localStorage.getItem(ELocalStorage.USER) || sessionStorage.getItem(ELocalStorage.USER);

  if (!storedUser) return;

  try {
    const parsedUser = JSON.parse(storedUser);

    if ('trackedCounterparties' in parsedUser && !('maxTrackedCounterparties' in parsedUser)) {
      return;
    }
  } catch {
    // fall through - an unparsable user is as unusable as an outdated one
  }

  clearStorage(localStorage);
  clearStorage(sessionStorage);
  clearSessionStartTime();
};

discardOutdatedStoredSession();

const setSessionStartTime = (rememberMe: boolean) => {
  const storage = rememberMe ? localStorage : sessionStorage;
  const oppositeStorage = rememberMe ? sessionStorage : localStorage;

  storage.setItem(ELocalStorage.AUTH_SESSION_STARTED_AT, Date.now().toString());
  oppositeStorage.removeItem(ESessionStorage.AUTH_SESSION_STARTED_AT);
};

const getSessionDurationInSeconds = () => {
  const sessionStartedAtRaw =
    localStorage.getItem(ELocalStorage.AUTH_SESSION_STARTED_AT) ||
    sessionStorage.getItem(ESessionStorage.AUTH_SESSION_STARTED_AT);

  if (!sessionStartedAtRaw) return undefined;

  const sessionStartedAt = Number(sessionStartedAtRaw);

  if (Number.isNaN(sessionStartedAt)) return undefined;

  return Math.max(0, Math.floor((Date.now() - sessionStartedAt) / 1000));
};

const mapRedirectToPathToVariant = (redirectTo: string | null) => {
  const defaultRedirectPathname = ERoutes.DASHBOARD;
  const redirectUrl = !!redirectTo ? redirectTo : defaultRedirectPathname;

  if (isRouteOrDescendant(redirectUrl, ERoutes.MONITORING_SETTINGS)) {
    return ERoutes.DASHBOARD_SETTINGS;
  }

  if (isRouteOrDescendant(redirectUrl, ERoutes.MONITORING_ENTITY)) {
    const requestedEntity = redirectUrl.split(`${ERoutes.MONITORING_ENTITY}/`)[1]?.split('/')[0];

    return requestedEntity ? `${ERoutes.ENTITY}/${requestedEntity}` : ERoutes.DASHBOARD;
  }

  if (isRouteOrDescendant(redirectUrl, ERoutes.RATINGS)) {
    if (!redirectUrl.includes('?')) {
      const optionsLength = redirectUrl.split('/').length;
      if (optionsLength === 2) {
        // Redirection for old links with the cex tab
        return ERoutes.DASHBOARD;
      }

      // Redirection for old links of the entity rating details view
      const requestedEntity = redirectUrl.split(`${ERoutes.RATINGS}`)[1];

      return !!requestedEntity ? `${ERoutes.ENTITY}${requestedEntity}` : ERoutes.DASHBOARD;
    }

    // Redirection for old links with the custody tab
    return ERoutes.DASHBOARD;
  }

  return isKnownRoute(redirectUrl) ? redirectUrl : defaultRedirectPathname;
};

export const useNewAuth = (): INewAuthContext => {
  const {
    logIn,
    loginError,
    isLoggingIn,
    logOut,
    token,
    user,
    isAuthenticated,
    setRememberMe,
    redirectLoggedInUser,
    refreshUserDataSet,
  } = useContext(NewAuthContext);

  assertIsDefined(logIn, 'logIn must be defined');
  assertIsDefined(logOut, 'logOut must be defined');
  assertIsDefined(setRememberMe, 'setRememberMe must be defined');
  assertIsDefined(redirectLoggedInUser, 'redirectLoggedInUser must be defined');
  assertIsDefined(refreshUserDataSet, 'refreshUserDataSet must be defined');

  return {
    logIn,
    loginError: loginError || null,
    isLoggingIn: !!isLoggingIn,
    logOut,
    token: token || null,
    user: user || null,
    isAuthenticated: !!isAuthenticated,
    setRememberMe,
    redirectLoggedInUser,
    refreshUserDataSet,
  };
};

export const useAuthManager = () => {
  const navigator = useNavigate();

  const [token, setToken] = useState<string | null | undefined>(() => getStoredToken());
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getStoredToken());
  //TODO: This is a temporary solution until we have the remember me functionality implemented, for that reason it is passed always as true.
  const [rememberMe, setRememberMe] = useState(true);
  const [user, setUser] = useState<IUser | null>(() => getStoredUser());

  const { resetTracking, trackLogin, trackRefresh, identifyUser, captureDailyAuthState } =
    useTracking();
  const { authApi } = createAuthApiClient();
  const { userApi } = createAuthUserApiClient();

  const updateToken = useCallback((newToken: string, rememberMe: boolean) => {
    setToken(newToken);

    const storage = rememberMe ? localStorage : sessionStorage;
    const oppositeStorage = rememberMe ? sessionStorage : localStorage;
    storage.setItem(ELocalStorage.ACCESS_TOKEN, newToken);
    clearStorage(oppositeStorage);
  }, []);

  const resetToken = useCallback(() => {
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  const updateUser = useCallback((user: IUser, rememberMe: boolean) => {
    setUser(user);

    const storage = rememberMe ? localStorage : sessionStorage;
    const oppositeStorage = rememberMe ? sessionStorage : localStorage;

    storage.setItem(ELocalStorage.USER, JSON.stringify(user));
    clearStorage(oppositeStorage);
  }, []);

  const resetUser = useCallback(() => {
    setUser(null);
  }, []);

  const redirectLoggedInUser = useCallback(
    (redirectTo: string) => {
      const redirectTarget = mapRedirectToPathToVariant(redirectTo);

      navigator(redirectTarget, { replace: true });
    },
    [navigator],
  );

  const { fetchingUser, isPending: isFetchingUser } = useGetUser(userApi);

  const {
    logInUser,
    error: loginError,
    isPending: isLogInRequestPending,
  } = useLogInUser(authApi, {
    onSuccess: authResponse => {
      hasLoggedOut = false;
      PendingRedirect.set(mapRedirectToPathToVariant(ReturnTo.peek()));
      updateToken(authResponse.access_token, rememberMe);
      setIsAuthenticated(true);
      fetchingUser(authResponse.access_token, {
        onSuccess: userDto => {
          const userDataToUpdate = {
            userName: userDto.firstName,
            userId: userDto.id,
            email: userDto.emailAddress,
            cohort: userDto.cohort,
            permissions: userDto.permissions,
            isBetaUser: userDto.isBetaUser,
            entitiesSettings: userDto.settings?.entities || null,
            trackedCounterparties: userDto.trackedCounterparties ?? null,
          };

          updateUser(userDataToUpdate, rememberMe);
          setSessionStartTime(rememberMe);
          identifyUser(userDto);
          const userPermissions = userDto.permissions.join(', ');
          trackLogin(userDto.emailAddress, userPermissions);
          captureDailyAuthState(userDto.id, 'explicit_login', 'form');
        },
        onError: error => {
          console.error('Fetching user after login failed: ', error.message);
        },
      });
    },
  });

  const isLoggingIn = isLogInRequestPending || isFetchingUser;

  const logIn = useCallback(
    (logInCredentials: ILogInRequest, onSuccess?: () => void, onError?: () => void) => {
      logInUser(logInCredentials, { onSuccess, onError });
    },
    [logInUser],
  );

  const logOut = useCallback(
    (reason: TLogoutReason = 'manual') => {
      if (hasLoggedOut) return;

      hasLoggedOut = true;

      const sessionDurationInSeconds = getSessionDurationInSeconds();
      resetTracking(reason, sessionDurationInSeconds);
      resetToken();
      clearStorage(localStorage);
      clearStorage(sessionStorage);
      clearSessionStartTime();
      resetUser();
      PendingRedirect.clear();
      queryClient.clear();
      redirectToLoginWithFullLoad();
    },
    [resetTracking, resetToken, resetUser],
  );

  const { refreshToken } = useRefreshToken(authApi, {
    onSuccess: authResponse => {
      updateToken(authResponse.access_token, rememberMe);
      setIsAuthenticated(true);
      trackRefresh('success', 'interval');
      if (user?.userId) {
        captureDailyAuthState(user.userId, 'carried_over', 'refresh_token');
      }
      queryClient.invalidateQueries();
    },
    onError: error => {
      trackRefresh('failed', 'interval', error.message);
      console.error('Refresh token failed: ', error.message);
      logOut('refresh_failed');
    },
  });

  const refreshAuthToken = useCallback(
    (onSuccess?: () => void, onError?: () => void) => {
      refreshToken(undefined, { onSuccess, onError });
    },
    [refreshToken],
  );

  useEffect(() => {
    const refreshTokenInterval = setInterval(
      () => {
        if (isAuthenticated) {
          refreshAuthToken();
        }
      },
      25 * 60 * 1000,
    ); // 25 minutes

    return () => clearInterval(refreshTokenInterval);
  }, [isAuthenticated, refreshAuthToken]);

  useEffect(() => {
    if (!token || !user?.userId || !user?.email) return;
    const { userId, email, cohort } = user;

    identifyUser({ id: userId, email, ...(cohort !== undefined && { cohort }) });
    captureDailyAuthState(userId, 'carried_over', 'refresh_token');
    // Fire once at app init for users restored from storage — login flow handles fresh logins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshUserDataSet = useCallback(
    (userDataToUpdate: IUser) => {
      updateUser(userDataToUpdate, rememberMe);
    },
    [rememberMe, updateUser],
  );

  return useMemo(
    () => ({
      logIn,
      loginError,
      isLoggingIn,
      logOut,
      token,
      user,
      isAuthenticated,
      setRememberMe,
      redirectLoggedInUser,
      refreshUserDataSet,
    }),
    [
      logIn,
      loginError,
      isLoggingIn,
      logOut,
      token,
      user,
      isAuthenticated,
      redirectLoggedInUser,
      refreshUserDataSet,
    ],
  );
};
